import { useCallback, useEffect } from "react";
import { applyCmtsPwaUpdate, runCmtsPwaCacheCleanup } from "../../pwa/serviceWorkerRegistration.js";
import { cmtsPwaRuntimeVersion } from "../core/pwa/cmtsPwaVersion.js";
import { pwaActions } from "../store/pwaSlice.js";
import { useCmtsDispatch, useCmtsSelector } from "../store/hooks.js";
import { selectPwaState } from "../store/selectors.js";

function readMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "CMTS PWA runtime operation failed.";
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}

export function useCmtsPwaUpdate() {
  const dispatch = useCmtsDispatch();
  const state = useCmtsSelector(selectPwaState);

  useEffect(() => {
    dispatch(pwaActions.pwaRuntimeVersionLoaded(cmtsPwaRuntimeVersion));
    const onUpdate = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail ?? {};
      dispatch(pwaActions.pwaUpdateReady({
        serviceWorkerVersion: typeof detail.serviceWorkerVersion === "string" ? detail.serviceWorkerVersion : cmtsPwaRuntimeVersion.serviceWorkerVersion,
        appVersion: typeof detail.appVersion === "string" ? detail.appVersion : cmtsPwaRuntimeVersion.appVersion,
      }));
    };
    const onCleanup = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail ?? {};
      dispatch(pwaActions.pwaCacheCleanupCompleted({ deletedCaches: asStringArray(detail.deletedCaches), preservedCaches: asStringArray(detail.preservedCaches) }));
    };
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail ?? {};
      dispatch(pwaActions.pwaErrorRecorded({ message: readMessage(detail.error ?? detail.message), code: "CMTS_PWA_RUNTIME_ERROR", atUtc: new Date().toISOString() }));
    };
    window.addEventListener("cmts:pwa-update", onUpdate);
    window.addEventListener("cmts:pwa-cache-cleanup-completed", onCleanup);
    window.addEventListener("cmts:pwa-error", onError);
    return () => {
      window.removeEventListener("cmts:pwa-update", onUpdate);
      window.removeEventListener("cmts:pwa-cache-cleanup-completed", onCleanup);
      window.removeEventListener("cmts:pwa-error", onError);
    };
  }, [dispatch]);

  const acceptUpdate = useCallback(async () => {
    dispatch(pwaActions.pwaUpdateAccepted());
    const posted = await applyCmtsPwaUpdate();
    if (!posted) dispatch(pwaActions.pwaCacheCleanupFailed({ errorMessage: "No waiting service worker is available for update activation." }));
    return posted;
  }, [dispatch]);

  const cleanupCaches = useCallback(async () => {
    dispatch(pwaActions.pwaCacheCleanupStarted());
    try {
      await runCmtsPwaCacheCleanup();
    } catch (error: unknown) {
      dispatch(pwaActions.pwaCacheCleanupFailed({ errorMessage: readMessage(error) }));
    }
  }, [dispatch]);

  const dismissUpdate = useCallback(() => dispatch(pwaActions.pwaUpdateDismissed()), [dispatch]);

  return { state, acceptUpdate, cleanupCaches, dismissUpdate };
}
